// src/components/common/AppNavbar.tsx

import { useEffect, useState } from "react";
import { Button, Dropdown, Navbar } from "react-daisyui";
import { useTranslation } from "react-i18next";
import { HiBell, HiCog, HiLogout, HiMenuAlt2, HiUser } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import Language from "@/components/common/Language";
import Megamenu from "@/components/common/MegaMenu";
import Theme from "@/components/common/Theme";
import { logout } from "@/redux/auth/authSlice";
import { authService } from "@/services/authService";
import notificationsService from "@/services/notificationsService";
import { RootState } from "@/store";

import SearchBar from "./SearchBar";

interface AppNavbarProps {
  onSidebarToggle?: () => void;
}

export default function AppNavbar({ onSidebarToggle }: AppNavbarProps = {}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread notifications count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await notificationsService.getStats();
        setUnreadCount(response.data.unread);
      } catch (error) {
        console.error("Failed to fetch notification stats:", error);
      }
    };

    fetchUnreadCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);

    // Listen for notification updates (delete/mark as read)
    window.addEventListener("notification-updated", fetchUnreadCount);

    return () => {
      clearInterval(interval);
      window.removeEventListener("notification-updated", fetchUnreadCount);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
      dispatch(logout());
      navigate("/auth/login");
    } catch (error) {
      console.error("Logout error:", error);
      // Even if API logout fails, clear local state
      dispatch(logout());
      navigate("/auth/login");
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      console.log("Searching for:", searchQuery);
      // Add search logic here
    }
  };

  const suggestions = [
    t("suggestions.dashboard", "Dashboard"),
    t("suggestions.orders", "Orders"),
    t("suggestions.users", "Users"),
    t("suggestions.analytics", "Analytics"),
    t("suggestions.reports", "Reports"),
    t("suggestions.settings", "Settings"),
    t("suggestions.billing", "Billing"),
  ];

  return (
    <>
      <Navbar
        className={twMerge(
          "fixed z-20 bg-base-100",
          "border-b border-b-base-200",
        )}
      >
        <Navbar.Start>
          <Button
            aria-label="Toggle sidebar"
            color="ghost"
            onClick={onSidebarToggle}
            shape="circle"
          >
            <HiMenuAlt2 className="h-5 w-5" />
          </Button>

          <Link className="ml-2 text-2xl font-bold" to="/">
            {import.meta.env.VITE_APP_NAME}
          </Link>
          <Navbar.Center className="hidden w-full justify-center px-4 lg:flex">
            <SearchBar
              onChange={setSearchQuery}
              onSubmit={handleSearch}
              suggestions={suggestions}
              value={searchQuery}
            />
          </Navbar.Center>
        </Navbar.Start>

        <Navbar.End>
          {/* Mobile search */}

          {/* Notification Icon */}
          <Button
            className="indicator mr-1"
            color="ghost"
            onClick={() => navigate("/dashboard/notifications")}
            shape="circle"
          >
            {unreadCount > 0 && (
              <span className="indicator-item badge badge-warning badge-sm">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
            <HiBell className="h-5 w-5" />
          </Button>

          <Language className="mr-1" />
          <Theme className="mr-1" />

          <Dropdown className="pr-3" end>
            <Dropdown.Toggle button={false}>
              <Button className="avatar" color="ghost" shape="circle">
                <div className="w-10 rounded-full">
                  <img
                    alt="Profile"
                    loading="lazy"
                    src="https://avatars.githubusercontent.com/u/21959017?s=400&u=1c2711bcd2713d682bf553835a6dce998c6fd49b&v=4"
                  />
                </div>
              </Button>
            </Dropdown.Toggle>

            <Dropdown.Menu className="menu-compact mt-3 w-52">
              <li className="border-b border-base-300 px-4 py-2">
                <div className="flex flex-col">
                  <span className="font-semibold">
                    {user ? `${user.firstName} ${user.lastName}` : "User"}
                  </span>
                  <span className="text-sm text-base-content/70">
                    {user?.email || "user@example.com"}
                  </span>
                </div>
              </li>
              <Dropdown.Item className="justify-between">
                <div className="flex items-center gap-2">
                  <HiUser className="h-4 w-4" />
                  {t("navbar.profile", "Profile")}
                </div>
                <span className="badge badge-primary badge-sm">
                  {t("navbar.new", "New")}
                </span>
              </Dropdown.Item>
              <Dropdown.Item onClick={() => navigate("/settings")}>
                <div className="flex items-center gap-2">
                  <HiCog className="h-4 w-4" />
                  {t("navbar.settings", "Settings")}
                </div>
              </Dropdown.Item>
              <Dropdown.Item onClick={handleLogout}>
                <div className="flex items-center gap-2 text-error">
                  <HiLogout className="h-4 w-4" />
                  {t("navbar.logout", "Logout")}
                </div>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Navbar.End>
      </Navbar>

      {/* Mobile search bar */}
      <div className="fixed left-0 right-0 top-16 z-10 border-b border-base-200 bg-base-100 p-4 lg:hidden">
        <SearchBar
          onChange={setSearchQuery}
          onSubmit={handleSearch}
          suggestions={suggestions}
          value={searchQuery}
        />
      </div>

      <Megamenu onClose={() => setMenuOpen(false)} open={menuOpen} />
    </>
  );
}
